<template>
  <div>
    <div
      v-for="(semester, semesterIndex) in academicRecord.semesters"
      :key="semesterIndex"
      class="border p-2 mb-2">
      <div class="d-flex align-items-center py-2">
        <span class="bg-grey03 p-2 fs-s mr-2">{{ semester.semester | semester }}</span>
        <div class="fs-m font-weight-bold">
          通知表
        </div>
      </div>
      <div class="d-flex justify-content-start">
        <FileViewer
          v-for="(file, fileIndex) in semester.files"
          :key="`semester-${semesterIndex}-file-${fileIndex}`"
          style="min-width: 0;"
          :file="file.file"
          :thumbnail="file.thumbnail" />
      </div>

      <div
        class="mb-2">
        <b-row
          class="mx-0"
          cols="5">
          <b-col
            v-for="(subjectScore, subjectScoreIndex) in semester.subjectScores"
            :key="subjectScoreIndex"
            class="fs-s text-center p-1">
            <label
              class="py-1 mb-0">{{ subjectScore.subjectName | subjectName }}</label>
            <div
              class="fs-s bg-grey03 p-2"
              style="min-height: 34px;">
              {{ subjectScore.score }}
            </div>
          </b-col>
        </b-row>
        <div
          v-if="isHighSchool"
          class="mb-2">
          <b-row
            class="mx-0"
            cols="3">
            <b-col class="fs-s text-center p-1">
              <label
                class="py-1 mb-0">成績計</label>
              <div class="fs-s bg-grey03 p-2">
                {{ getSumOfSubjectScores(semester) }}
              </div>
            </b-col>
            <b-col class="fs-s text-center p-1">
              <label
                class="py-1 mb-0">成績平均</label>
              <div class="fs-s bg-grey03 p-2">
                {{ getAverageOfSubjectScores(semester) }}
              </div>
            </b-col>
          </b-row>
        </div>
        <div
          v-else-if="isJuniorHighSchool"
          class="mb-2">
          <b-row
            class="mx-0"
            cols="3">
            <b-col class="fs-s text-center p-1">
              <label
                class="py-1 mb-0">5教科成績計</label>
              <div class="fs-s bg-grey03 p-2">
                {{ getSumOf5SubjectScores(semester) }}
              </div>
            </b-col>
            <b-col class="fs-s text-center p-1">
              <label
                class="py-1 mb-0">9教科成績計</label>
              <div class="fs-s bg-grey03 p-2">
                {{ getSumOfSubjectScores(semester) }}
              </div>
            </b-col>
            <b-col class="fs-s text-center p-1">
              <label
                class="py-1 mb-0">成績平均</label>
              <div class="fs-s bg-grey03 p-2">
                {{ getAverageOfSubjectScores(semester) }}
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
      <div>
        <b-row class="table-row mx-0">
          <b-col class="table-cell">
            <label class="table-cell__header py-2 mb-0 fs-s">出席日数</label>
            <div class="py-2 fs-s">
              {{ semester.attendanceDays }}
            </div>
          </b-col>
          <b-col class="table-cell">
            <label class="table-cell__header py-2 mb-0 fs-s">欠席日数</label>
            <div class="py-2 fs-s">
              {{ semester.absenceDays }}
            </div>
          </b-col>
          <b-col class="table-cell">
            <label class="table-cell__header py-2 mb-0 fs-s">遅刻日数</label>
            <div class="py-2 fs-s">
              {{ semester.lateDays }}
            </div>
          </b-col>
          <b-col class="table-cell">
            <label class="table-cell__header py-2 mb-0 fs-s">早退日数</label>
            <div class="py-2 fs-s">
              {{ semester.earlyLeaveDays }}
            </div>
          </b-col>
          <b-col class="table-cell">
            <label class="table-cell__header py-2 mb-0 fs-s">忌引日数</label>
            <div class="py-2 fs-s">
              {{ semester.exclusionDays }}
            </div>
          </b-col>
        </b-row>
      </div>

      <div
        v-if="canInputTestResults && semester.testResults.length > 0"
        class="mb-2">
        <hr class="my-4">
        <div class="d-flex align-items-center pb-2">
          <span class="bg-grey03 p-2 fs-s mr-2">{{ semester.semester | semester }}</span>
          <div class="fs-m font-weight-bold">
            テスト結果
          </div>
        </div>

        <div
          v-for="(testResult, testResultIndex) in semester.testResults"
          :key="testResultIndex">
          <div class="d-flex justify-content-start align-items-center mb-2">
            <div class="fs-s bg-grey03 p-2">
              {{ testResult.testName | testName }}
            </div>
            <div class="fs-s ml-2">
              {{ testResult.testDate | prettyDate }}
            </div>
          </div>
          
          <div
            v-if="isHighSchool"
            class="mb-2">
            <b-row
              class="mx-0"
              cols="3">
              <b-col class="fs-s text-center p-1">
                <label
                  class="py-1 mb-0">テスト合計点</label>
                <div class="fs-s bg-grey03 p-2">
                  {{ getSumOfTestResultsSubjectScores(testResult) }}
                </div>
              </b-col>
              <b-col class="fs-s text-center p-1">
                <label
                  class="py-1 mb-0">テスト平均点</label>
                <div class="fs-s bg-grey03 p-2">
                  {{ getAverageOfTestResultsSubjectScores(testResult) }}
                </div>
              </b-col>
            </b-row>
          </div>
          <div
            v-else-if="isJuniorHighSchool"
            class="mb-2">
            <b-row
              class="mx-0"
              cols="3">
              <b-col class="fs-s text-center p-1">
                <label
                  class="py-1 mb-0">5教科合計点</label>
                <div class="fs-s bg-grey03 p-2">
                  {{ getSumOfTestResults5SubjectScores(testResult) }}
                </div>
              </b-col>
              <b-col class="fs-s text-center p-1">
                <label
                  class="py-1 mb-0">9教科合計点</label>
                <div class="fs-s bg-grey03 p-2">
                  {{ getSumOfTestResultsSubjectScores(testResult) }}
                </div>
              </b-col>
              <b-col class="fs-s text-center p-1">
                <label
                  class="py-1 mb-0">テスト平均点</label>
                <div class="fs-s bg-grey03 p-2">
                  {{ getAverageOfTestResultsSubjectScores(testResult) }}
                </div>
              </b-col>
            </b-row>
          </div>

          <div>
            <b-row
              class="table-row mx-0"
              cols="5">
              <b-col
                v-for="(subjectScore, subjectScoreIndex) in testResult.subjectScores"
                :key="subjectScoreIndex"
                class="table-cell">
                <label
                  class="table-cell__header py-2 mb-0 fs-s">{{ subjectScore.subjectName | subjectName }}</label>
                <div class="fs-s py-2">
                  {{ subjectScore.score | trimDecimalZero }}
                </div>
              </b-col>
            </b-row>
          </div>
          <hr
            v-if="testResultIndex !== semester.testResults.length - 1"
            class="my-2">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FileViewer from '@/components/parts/FileViewer.vue'
import { isJuniorHighSchool, isHighSchool, juniorHighSchool5SubjectTitles, canInputTestResults } from '@/helper/profile'
export default {
  components: {
    FileViewer,
  },
  props: {
    academicRecord: {
      type: Object,
      default: null
    }
  },
  computed: {
    isJuniorHighSchool() {
      return isJuniorHighSchool(this.academicRecord.gradeCode)
    },
    isHighSchool() {
      return isHighSchool(this.academicRecord.gradeCode)
    },
    sumOfSubjectScores() {
      return this.academicRecord.semesters.reduce((prev, curr) => {
        return prev + curr.subjectScores.reduce((prev, curr) => {
          return prev + curr.score
        }, 0)
      }, 0)
    },
    averageOfSubjectScores() {
      return this.sumOfSubjectScores / this.academicRecord.semesters.reduce((prev, curr) => {
        return prev + curr.subjectScores.length
      }, 0)
    },
    canInputTestResults() {
      return canInputTestResults(this.academicRecord.gradeCode)
    },
  },
  methods: {
    // 有効な成績・テスト結果のみをフィルタリング
    getValidSubjectScores(subjectScores) {
      return subjectScores.filter(subjectScore => subjectScore.score !== null)
    },
    // 全教科合計
    getSumOfSubjectScores(semester) {
      const validSubjectScores = this.getValidSubjectScores(semester.subjectScores)
      return validSubjectScores.reduce((prev, curr) => {
        return prev + curr.score
      }, 0)
    },
    // 全教科平均
    getAverageOfSubjectScores(semester) {
      // 未入力の教科を除外
      const validSubjectScores = this.getValidSubjectScores(semester.subjectScores)
      // 全教科が未入力の場合は0を返す
      if (validSubjectScores.length === 0) {
        return 0
      }
      return (this.getSumOfSubjectScores(semester) / validSubjectScores.length).toFixed(1)
    },
    // 5教科合計
    getSumOf5SubjectScores(semester) {
      const validSubjectScores = this.getValidSubjectScores(semester.subjectScores)
      return validSubjectScores.reduce((prev, curr) => {
        if (juniorHighSchool5SubjectTitles.includes(curr.subjectName)) {
          return prev + curr.score
        }
        return prev
      }, 0)
    },
    // テスト点数5教科合計
    getSumOfTestResults5SubjectScores(testResult) {
      const validSubjectScores = this.getValidSubjectScores(testResult.subjectScores)
      return validSubjectScores.reduce((prev, curr) => {
        if (juniorHighSchool5SubjectTitles.includes(curr.subjectName)) {
          return prev + Number(curr.score)
        }
        return prev
      }, 0)
    },
    // テスト点数全教科合計
    getSumOfTestResultsSubjectScores(testResult) {
      const validSubjectScores = this.getValidSubjectScores(testResult.subjectScores)
      return validSubjectScores.reduce((prev, curr) => {
        return prev + Number(curr.score)
      }, 0)
    },
    // テスト点数全教科平均
    getAverageOfTestResultsSubjectScores(testResult) {
      // 未入力の教科を除外
      const validSubjectScores = this.getValidSubjectScores(testResult.subjectScores)
      // 全教科が未入力の場合は0を返す
      if (validSubjectScores.length === 0) {
        return 0
      }
      return (this.getSumOfTestResultsSubjectScores(testResult) / validSubjectScores.length).toFixed(1)
    },
  }
}
</script>

<style lang="scss" scoped>
.table-row {
  display: flex;
  align-items: center;
}

.table-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  height: 68px;

  &__header {
    width: 100%;
    text-align: center;
    border-bottom: 1px solid $grey11;
  }

  // cellに枠線をつける
  &:after {
    position: absolute;
    box-sizing: border-box;
    top: -0.5px;
    left: -0.5px;
    content: '';
    width: calc(100% + 1px);
    height: calc(100% + 1px);
    border: 1px solid $grey11;
    pointer-events: none;
  }
}

</style>